import { useEffect, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getUnit } from './api'

/**
 * A polling station as the hierarchy a human reads: **Kenya → Nairobi → Westlands →
 * Kangemi → Wilkins Primary**.
 *
 * Lives in `geography` because the units are geography's, and two features now need
 * this: a transfer's From/To and a voter's registered placement. The alternative — one
 * copy in each — is two copies that drift.
 *
 * The APIs carry an ltree `path` — `KE.047.047_WES.047_WES_KANGEMI.…` — which
 * is the right thing to store, to index and to filter a scope by, and the wrong thing to
 * put in front of a reviewer. Nobody signs their name to a decision about
 * `047_WES_KANGEMI`. The codes are still there, on hover, because someone reconciling
 * against an IEBC spreadsheet needs them.
 *
 * ## Where the names come from
 *
 * `GET /geography/units/{id}` returns the unit **and its ancestors, root-first** — so one
 * call resolves the whole line. Results are cached for the session in a module-level map:
 * a queue page can hold forty requests pointing at six stations, and the same six lookups
 * repeated per row is a slow screen on a field connection.
 *
 * ## When the caller already has the line
 *
 * `GET /geography/units/{id}` returns the ancestors anyway, so a screen that has just
 * loaded a unit detail already holds every name. Passing them as `levels` renders the same
 * chain with no second request — the unit drawer would otherwise fetch the unit it is
 * already showing.
 *
 * ## When it cannot resolve
 *
 * The code path renders instead. That is deliberate and not a placeholder: a station
 * outside the viewer's geographic scope, or one merged away, genuinely has no name this
 * caller may see, and showing the code is more honest than an empty row or a spinner that
 * never resolves. `path` is also the only thing present at all when there is no station
 * id, which is every historical row written before the id was carried.
 */

interface Resolved { levels: { code: string; name: string }[] }

const cache = new Map<string, Promise<Resolved | null>>()

function resolveUnit(id: string): Promise<Resolved | null> {
  const hit = cache.get(id)
  if (hit) return hit
  const p = getUnit(id)
    .then((d): Resolved => ({
      // REVERSED: the route returns ancestors nearest-first — ward, constituency, county,
      // country — and a reader expects the opposite. Without this the chain renders
      // inside-out, which looks plausible enough at a glance to survive review.
      levels: [
        ...[...d.ancestors].reverse().map((a) => ({ code: a.code, name: a.name })),
        { code: d.unit.code, name: d.unit.name },
      ],
    }))
    // A failure is cached as "no name" on purpose. Retrying a 403 once per render is
    // how a scope boundary turns into a request storm.
    .catch(() => null)
  cache.set(id, p)
  return p
}

export function UnitPath({
  stationId, path, levels, className,
}: {
  stationId?: string | null
  path?: string | null
  /** Root-first, including the unit itself. Supplied when the caller already has them. */
  levels?: { code: string; name: string }[] | null
  className?: string
}) {
  const [fetched, setFetched] = useState<Resolved | null>(null)

  useEffect(() => {
    let alive = true
    // Nothing to fetch when the caller handed the line over.
    if (levels?.length || !stationId) { setFetched(null); return }
    void resolveUnit(stationId).then((r) => { if (alive) setFetched(r) })
    return () => { alive = false }
  }, [stationId, levels])

  const resolved: Resolved | null = levels?.length ? { levels } : fetched

  const codes = (path ?? '').split('.').filter(Boolean)

  if (!resolved) {
    if (codes.length === 0) return <span className="text-muted-foreground">—</span>
    return (
      <span className={cn('inline-flex items-center gap-1 flex-wrap justify-end', className)}>
        {codes.map((c, i) => (
          <span key={`${c}-${i}`} className="inline-flex items-center gap-1">
            <code className={cn('font-mono text-erp-xs',
              i === codes.length - 1 && 'font-semibold')}>{c}</code>
            {i < codes.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground/50" />}
          </span>
        ))}
      </span>
    )
  }

  return (
    <span
      className={cn('inline-flex items-center gap-1 flex-wrap justify-end', className)}
      title={resolved.levels.map((l) => l.code).join(' · ')}
    >
      {resolved.levels.map((l, i) => (
        <span key={`${l.code}-${i}`} className="inline-flex items-center gap-1">
          <span className={cn(
            'text-erp-sm',
            i === resolved.levels.length - 1
              ? 'font-semibold'
              : 'text-muted-foreground',
          )}>
            {l.name}
          </span>
          {i < resolved.levels.length - 1 && (
            <ArrowRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
          )}
        </span>
      ))}
    </span>
  )
}
